import { Typography } from '@/components/atoms/Typography';
import {
  DownloadIcon,
  FileIcon,
  MasterPlanIcon,
} from '@/components/atoms/icons';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { isSmallScreen } from '@/core/utils/responsive';
import React from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

export type UnitDocumentCardAction = 'download' | 'view';

export interface UnitDocumentItemProps {
  title: string;
  onPress?: () => void;
  /** Shows a small spinner beside the trailing action while the file is fetching. */
  isDownloading?: boolean;
  /**
   * Trailing control: download icon (brochure) or underlined “View” (master plan).
   * @default 'download'
   */
  action?: UnitDocumentCardAction;
  /** CTA label when `action` is `view` (Contentful `view` / local `*.view`). */
  actionLabel?: string;
}

/**
 * Document card for the web catalogue.
 * Guest Documents accordion uses download rows; ADHA white-card section may use `view`.
 */
export function UnitDocumentItem({
  title,
  onPress,
  isDownloading = false,
  action = 'download',
  actionLabel,
}: Readonly<UnitDocumentItemProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const isMobile = isSmallScreen();
  const isViewAction = action === 'view';
  const LeadingIcon = isViewAction ? MasterPlanIcon : FileIcon;
  const viewLabel = actionLabel?.trim() || 'View';

  return (
    <TouchableOpacity
      style={[
        styles.container,
        {
          // Guest parity: level1 inside accordion. ADHA view rows also fine on white card.
          backgroundColor: colors.backgroundLevel1,
          borderColor: colors.backgroundLevel4,
        },
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      disabled={isDownloading}
      accessibilityRole="button"
      accessibilityLabel={isViewAction ? `${title}, ${viewLabel}` : title}
      accessibilityState={{ busy: isDownloading, disabled: isDownloading }}
    >
      <LeadingIcon size={24} color={colors.fontPrimary} />
      <Typography
        variant={isMobile ? 'body' : 'bodyLarge'}
        color="primary"
        style={[styles.title, isRTL && styles.titleRTL]}
      >
        {title}
      </Typography>
      <View style={styles.actionSlot}>
        {isDownloading ? (
          <ActivityIndicator size="small" color={colors.fontPrimary} />
        ) : null}
        {isViewAction ? (
          <Typography
            variant={isMobile ? 'body' : 'bodyLarge'}
            style={[
              styles.viewLink,
              {
                color: colors.bloomRed,
                opacity: isDownloading ? 0.5 : 1,
              },
            ]}
            numberOfLines={1}
          >
            {viewLabel}
          </Typography>
        ) : (
          <DownloadIcon
            size={24}
            color={isDownloading ? colors.fontTertiary : colors.fontPrimary}
          />
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  title: {
    flex: 1,
    minWidth: 0,
  },
  titleRTL: {
    textAlign: 'right',
  },
  actionSlot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  viewLink: {
    fontFamily: 'Matter-Medium',
    fontWeight: '500',
    textDecorationLine: 'underline',
  },
});

export default UnitDocumentItem;
